import React, { useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaExternalLinkAlt,
  FaLink,
  FaRedo,
  FaSearch,
  FaSitemap,
} from "react-icons/fa";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(/\/$/, "");

const COLORS = {
  bg: "#FFF9F7",
  navy: "#1F2D4D",
  ink: "#181820",
  panel: "#FFFFFF",
  accent: "#FA5735",
  accentSoft: "#FFF0EB",
  green: "#16845B",
  muted: "#778095",
  border: "#DFE4EC",
};

const escapeCsv = (value) => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatNumber = (value) => safeNumber(value).toLocaleString();

const normalizeApiResult = (payload) => {
  const data = payload && typeof payload === "object" ? payload : {};
  const rawUrls = Array.isArray(data.urls)
    ? data.urls
    : Array.isArray(data.links)
      ? data.links
      : Array.isArray(data.results)
        ? data.results
        : [];

  const urls = rawUrls
    .map((item) => {
      if (typeof item === "string") {
        return {
          url: item,
          derivedTitle: "",
          derivedKeywords: [],
          lastmod: "",
          changefreq: "",
          priority: "",
          sourceSitemap: data.sitemapUrl || "",
        };
      }

      if (!item || typeof item !== "object") return null;
      const extractedUrl = item.url || item.loc || item.link || "";
      const keywords = Array.isArray(item.derivedKeywords)
        ? item.derivedKeywords
        : typeof item.derivedKeywords === "string"
          ? item.derivedKeywords.split(",").map((keyword) => keyword.trim()).filter(Boolean)
          : Array.isArray(item.keywords)
            ? item.keywords
            : typeof item.keywords === "string"
              ? item.keywords.split(",").map((keyword) => keyword.trim()).filter(Boolean)
              : [];

      return {
        ...item,
        url: extractedUrl,
        derivedTitle: item.derivedTitle || item.title || "",
        derivedKeywords: keywords,
        lastmod: item.lastmod || item.lastModified || "",
        changefreq: item.changefreq || item.changeFrequency || "",
        priority: item.priority == null ? "" : String(item.priority),
        sourceSitemap: item.sourceSitemap || data.sitemapUrl || "",
      };
    })
    .filter((item) => item && item.url);

  const warnings = Array.isArray(data.warnings) ? data.warnings.filter(Boolean) : [];
  const totalUrls = safeNumber(data.totalUrls, urls.length);
  const sitemapFilesProcessed = safeNumber(
    data.sitemapFilesProcessed ?? data.sitemapsProcessed ?? data.sitemapCount,
    urls.length || data.sitemapUrl ? 1 : 0
  );

  return {
    ...data,
    sitemapUrl: data.sitemapUrl || data.url || "",
    urls,
    warnings,
    totalUrls,
    sitemapFilesProcessed,
    truncated: Boolean(data.truncated),
  };
};

const XmlSitemapUrlExtractor = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");

  const filteredUrls = useMemo(() => {
    if (!result) return [];
    const query = search.trim().toLowerCase();
    if (!query) return result.urls;
    return result.urls.filter((item) =>
      [item.url, item.derivedTitle, item.derivedKeywords.join(" "), item.lastmod, item.changefreq]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [result, search]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const inputUrl = url.trim();
    if (!inputUrl) {
      setError("Please enter a website or XML sitemap URL.");
      toast.error("Please enter a sitemap URL.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setSearch("");
    console.log("[SITEMAP EXTRACTOR UI] Request started.", { inputUrl });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/sitemap-extractor`, { url: inputUrl }, { timeout: 120000 });
      if (!response.data || response.data.success === false) throw new Error(response.data?.message || "Invalid API response.");
      const normalizedResult = normalizeApiResult(response.data.data || response.data);
      setResult(normalizedResult);
      console.log("[SITEMAP EXTRACTOR UI] Request completed.", {
        sitemapUrl: normalizedResult.sitemapUrl,
        totalUrls: normalizedResult.totalUrls,
        sitemapFilesProcessed: normalizedResult.sitemapFilesProcessed,
        warnings: normalizedResult.warnings.length,
      });
      toast.success(`${formatNumber(normalizedResult.totalUrls)} unique URL(s) extracted!`, { theme: "colored" });
    } catch (requestError) {
      const message = requestError.response?.data?.message || (requestError.code === "ECONNABORTED" ? "The request timed out." : requestError.message) || "Failed to extract the sitemap.";
      setError(message);
      console.error("[SITEMAP EXTRACTOR UI] Request failed:", requestError);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUrl("");
    setResult(null);
    setError("");
    setSearch("");
    console.log("[SITEMAP EXTRACTOR UI] Reset complete.");
    toast.info("Extractor reset.");
  };

  const copyUrls = async () => {
    if (!filteredUrls.length) return;
    try {
      await navigator.clipboard.writeText(filteredUrls.map((item) => item.url).join("\n"));
      toast.success(`${formatNumber(filteredUrls.length)} URL(s) copied!`, { theme: "colored" });
    } catch (copyError) {
      console.error("[SITEMAP EXTRACTOR UI] Copy failed:", copyError);
      toast.error("Failed to copy URLs.");
    }
  };

  const downloadCsv = () => {
    if (!filteredUrls.length) return;
    try {
      const header = ["URL", "URL-derived title", "URL-derived keywords", "Last modified", "Change frequency", "Priority", "Source sitemap"];
      const rows = filteredUrls.map((item) => [item.url, item.derivedTitle, item.derivedKeywords.join(", "), item.lastmod, item.changefreq, item.priority, item.sourceSitemap]);
      const csv = `\uFEFF${[header, ...rows].map((row) => row.map(escapeCsv).join(",")).join("\r\n")}`;
      const objectUrl = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "sitemap-urls.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      console.log("[SITEMAP EXTRACTOR UI] CSV downloaded.", { rows: filteredUrls.length });
      toast.success("CSV downloaded!", { theme: "colored" });
    } catch (downloadError) {
      console.error("[SITEMAP EXTRACTOR UI] CSV download failed:", downloadError);
      toast.error("Failed to download CSV.");
    }
  };

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", padding: "52px 16px" }}>
      <ToastContainer />
      <div className="container-fluid">
       

        <div style={{ background: "rgba(255,255,255,0.55)", border: `1px solid ${COLORS.border}`, borderRadius: 22, padding: 32 }}>
          <div className="row g-4">
            <div className="col-lg-4">
              <div style={{ padding: "0 2px" }}>
                <h3 style={{ color: COLORS.ink, fontSize: 23, fontWeight: 850, marginBottom: 24 }}><FaSitemap color={COLORS.accent} size={21} /> Sitemap Extractor</h3>
                <p style={{ color: COLORS.muted, fontSize: 13.5, lineHeight: 1.6, marginBottom: 22 }}>Enter a website root or a direct XML sitemap URL. Root URLs automatically try `/sitemap.xml` and `/sitemap_index.xml`.</p>
                <form onSubmit={handleSubmit}>
                  <label className="form-label small fw-bold"><FaLink color={COLORS.accent} /> Sitemap URL</label>
                  <input className="form-control" value={url} disabled={loading} onChange={(event) => { setUrl(event.target.value); setError(""); }} placeholder="https://example.com/sitemap.xml" style={{ height: 52 }} />
                  {error ? <div style={{ color: COLORS.accent, background: COLORS.accentSoft, borderRadius: 8, padding: "9px 10px", fontSize: 12, marginTop: 10 }}>{error}</div> : null}
                  <button type="submit" disabled={loading} style={{ width: "100%", border: "none", borderRadius: 999, background: COLORS.accent, color: "#fff", fontWeight: 800, padding: "15px 18px", marginTop: 22, boxShadow: "0 6px 14px rgba(250,87,53,0.22)" }}>
                    <FaSearch /> {loading ? "Extracting…" : "Extract Sitemap URLs"}
                  </button>
                </form>
                <button type="button" onClick={handleReset} disabled={loading} className="btn btn-sm btn-link text-secondary text-decoration-none mt-2"><FaRedo size={10} /> Reset</button>
                <div style={{ background: COLORS.accentSoft, borderRadius: 10, padding: 12, color: "#7B4B42", fontSize: 11.5, lineHeight: 1.55, marginTop: 18 }}>
                  Titles and keywords shown in results are derived from each URL path. They are not claimed as page metadata.
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 24, minHeight: 500, boxShadow: "0 10px 22px rgba(31,45,77,0.10)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 17, marginBottom: 16 }}>
                  <h4 style={{ fontSize: 17, fontWeight: 850, margin: 0 }}><span style={{ color: "#F58A76" }}>●</span> Extracted Data</h4>
                  {result ? <div style={{ display: "flex", gap: 7 }}><button type="button" onClick={copyUrls} className="btn btn-sm btn-outline-secondary"><FaCopy size={10} /> Copy</button><button type="button" onClick={downloadCsv} className="btn btn-sm" style={{ background: COLORS.accent, color: "#fff" }}><FaDownload size={10} /> CSV</button></div> : null}
                </div>

                {!result && !loading ? (
                  <div style={{ border: `1px solid #EDF0F4`, borderRadius: 10, minHeight: 390, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#97A0B2" }}>
                    <div><div style={{ width: 65, height: 65, display: "grid", placeItems: "center", borderRadius: "50%", background: "#F8F9FB", margin: "0 auto 16px" }}><FaSitemap size={28} color="#D7DCE5" /></div><div>Enter a Sitemap URL to see results here.</div></div>
                  </div>
                ) : null}

                {loading ? (
                  <div style={{ minHeight: 390, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                    <div><div className="spinner-border" style={{ color: COLORS.accent }} role="status" /><div style={{ color: COLORS.muted, marginTop: 14 }}>Fetching and parsing sitemap files…</div></div>
                  </div>
                ) : null}

                {result ? (
                  <div>
                    <div className="row g-2 mb-3">
                      <div className="col-4"><div style={{ background: "#F8F9FB", borderRadius: 9, padding: 10 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>UNIQUE URLS</div><strong>{formatNumber(result.totalUrls)}</strong></div></div>
                      <div className="col-4"><div style={{ background: "#F8F9FB", borderRadius: 9, padding: 10 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>SITEMAPS</div><strong>{formatNumber(result.sitemapFilesProcessed)}</strong></div></div>
                      <div className="col-4"><div style={{ background: "#F8F9FB", borderRadius: 9, padding: 10 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>WARNINGS</div><strong>{formatNumber(result.warnings.length)}</strong></div></div>
                    </div>

                    <div style={{ position: "relative", marginBottom: 12 }}><FaSearch style={{ position: "absolute", left: 12, top: 12, color: COLORS.muted }} size={11} /><input className="form-control form-control-sm" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Filter URLs, titles, keywords or dates" style={{ paddingLeft: 32 }} /></div>

                    {result.warnings.length ? <div style={{ background: "#FFF6DF", borderRadius: 8, padding: "9px 11px", color: "#8A6515", fontSize: 11.5, marginBottom: 12 }}>{result.warnings.slice(0, 3).map((warning) => <div key={warning}>• {warning}</div>)}</div> : null}

                    {filteredUrls.length ? (
                      <div style={{ maxHeight: 430, overflow: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 9 }}>
                        <table className="table table-hover table-sm mb-0" style={{ fontSize: 11.5 }}>
                          <thead style={{ position: "sticky", top: 0, background: "#F8F9FB", zIndex: 1 }}><tr><th style={{ minWidth: 300 }}>URL</th><th style={{ minWidth: 150 }}>URL title</th><th style={{ minWidth: 180 }}>URL keywords</th><th style={{ minWidth: 105 }}>Last modified</th></tr></thead>
                          <tbody>{filteredUrls.map((item, index) => <tr key={`${item.url}-${index}`}><td><a href={item.url} target="_blank" rel="noreferrer" style={{ color: COLORS.navy, wordBreak: "break-all" }}>{item.url} <FaExternalLinkAlt size={8} /></a></td><td>{item.derivedTitle || "—"}</td><td>{item.derivedKeywords.length ? item.derivedKeywords.join(", ") : "—"}</td><td>{item.lastmod || "—"}</td></tr>)}</tbody>
                        </table>
                      </div>
                    ) : <div style={{ textAlign: "center", color: COLORS.muted, padding: 40 }}>No URLs match your filter.</div>}

                    <div style={{ color: COLORS.green, fontSize: 11.5, marginTop: 10 }}><FaCheckCircle size={10} /> Showing {formatNumber(filteredUrls.length)} of {formatNumber(result.totalUrls)} URL(s).</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlSitemapUrlExtractor;
